import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [preferences, setPreferences] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const contentPreferences = [
    { id: 'technical', label: 'Technical Tutorials', icon: 'Code' },
    { id: 'industry', label: 'Industry Insights', icon: 'TrendingUp' },
    { id: 'tools', label: 'Development Tools', icon: 'Wrench' },
    { id: 'ai', label: 'AI & Machine Learning', icon: 'Brain' }
  ];

  const handlePreferenceToggle = (preferenceId) => {
    setPreferences(prev => 
      prev?.includes(preferenceId)
        ? prev?.filter(id => id !== preferenceId)
        : [...prev, preferenceId]
    );
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email) return;

    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubscribed(true);
    setIsLoading(false);
  };

  if (isSubscribed) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative bg-gradient-to-br from-success/10 via-card to-accent/5 border border-success/20 rounded-3xl p-8 text-center shadow-3d"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <Icon name="CheckCircle" size={32} className="text-success" />
        </motion.div>
        <h3 className="text-2xl font-bold text-foreground mb-2">
          Welcome to the Community! 🎉
        </h3>
        <p className="text-muted-foreground mb-4">
          You're now subscribed to our newsletter. Get ready for amazing content!
        </p>
        <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Icon name="Users" size={16} />
            <span>10K+ subscribers</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Mail" size={16} />
            <span>Weekly updates</span>
          </div>
        </div>
        {/* Floating celebration elements */}
        <motion.div
          className="absolute -top-2 -right-2 w-4 h-4 bg-success/30 rounded-full"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.8, 0.3],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-2 -left-2 w-3 h-3 bg-accent/30 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative bg-gradient-to-br from-accent/5 via-card to-success/5 border border-border rounded-3xl p-8 shadow-3d"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-4 left-4 w-8 h-8 border border-accent rounded-lg rotate-12" />
        <div className="absolute bottom-4 right-4 w-6 h-6 border border-success rounded-full" />
        <div className="absolute top-1/2 right-8 w-4 h-4 bg-warning/20 rounded-full" />
      </div>
      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 bg-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-4"
          >
            <Icon name="Mail" size={32} className="text-accent" />
          </motion.div>
          
          <h3 className="text-2xl font-bold text-foreground mb-2">
            Stay in the Loop
          </h3>
          <p className="text-muted-foreground">
            Get the latest insights, tutorials, and industry trends delivered to your inbox
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="email"
            label="Email Address"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e?.target?.value)}
            required
            className="mb-6"
          />

          {/* Content Preferences */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-4">
              Content Preferences (Optional)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {contentPreferences?.map((preference, index) => (
                <motion.button
                  key={preference?.id}
                  type="button"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handlePreferenceToggle(preference?.id)}
                  className={`flex items-center space-x-3 p-3 rounded-xl border transition-all duration-300 ${
                    preferences?.includes(preference?.id)
                      ? 'bg-accent/10 border-accent/30 text-accent' :'bg-muted/30 border-border text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    preferences?.includes(preference?.id)
                      ? 'bg-accent/20' :'bg-muted'
                  }`}>
                    <Icon name={preference?.icon} size={16} />
                  </div>
                  <span className="text-sm font-medium">{preference?.label}</span>
                  {preferences?.includes(preference?.id) && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="ml-auto"
                    >
                      <Icon name="Check" size={16} />
                    </motion.div>
                  )}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isLoading}
            iconName="Send"
            iconPosition="right"
            className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent"
          >
            {isLoading ? 'Subscribing...' : 'Subscribe Now'}
          </Button>
        </form>

        {/* Trust Indicators */}
        <div className="flex items-center justify-center space-x-6 mt-6 pt-6 border-t border-border text-xs text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Icon name="Shield" size={14} />
            <span>No spam, ever</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Users" size={14} />
            <span>10K+ subscribers</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Zap" size={14} />
            <span>Unsubscribe anytime</span>
          </div>
        </div>
      </div>
      {/* 3D Floating Elements */}
      <motion.div
        className="absolute top-6 right-6 opacity-20"
        animate={{
          y: [0, -10, 0],
          rotate: [0, 5, -5, 0],
        }}
        transition={{ duration: 4, repeat: Infinity }}
      >
        <div className="bg-accent/20 rounded-lg p-2">
          <Icon name="BookOpen" size={16} className="text-accent" />
        </div>
      </motion.div>
      <motion.div
        className="absolute bottom-6 left-6 opacity-20"
        animate={{
          y: [0, 10, 0],
          rotate: [0, -5, 5, 0],
        }}
        transition={{ duration: 5, repeat: Infinity }}
      >
        <div className="bg-success/20 rounded-full p-2">
          <Icon name="Lightbulb" size={16} className="text-success" />
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NewsletterSignup;